// @ts-expect-error - @rails/activestorage has no type declarations
import { FileChecksum } from '@rails/activestorage/src/file_checksum'
import { useMemo, useState } from 'react'

import {
  BATCH_SIZE,
  type PresignedBlob,
  type Progress,
  type UseDirectUploadReturn,
} from '@/modules/upload'
import { batch } from '@/utils'
import { calculateChecksumFactory } from './calculateChecksumFactory'
import { presignBlobsFactory } from './presignBlobsFactory'
import { uploadFile } from './uploadFile'
import { buildProgress, sumProgress, updateFileCollection } from './utils'

export const useUpload = (): UseDirectUploadReturn => {
  const calculateChecksum = calculateChecksumFactory(FileChecksum.create)
  const presignBlobs = presignBlobsFactory(calculateChecksum)
  const [errorsByFile, setErrorsByFile] = useState<(Error | null)[]>([])
  const [progressByFile, setProgressByFile] = useState<Progress[]>([])

  const progress = useMemo(
    () => buildProgress(sumProgress(progressByFile)),
    [progressByFile],
  )

  const initUpload = (files: File[]): void => {
    setProgressByFile(
      files.map(({ size }) => ({ loaded: 0, percent: 0, total: size })),
    )
    setErrorsByFile(files.map(() => null))
  }

  const uploadFiles = async (
    files: File[],
    blobs: PresignedBlob[],
  ): Promise<void> => {
    for (let i = 0; i < blobs.length; i++) {
      const { direct_upload_url, headers } = blobs[i]

      const onProgress = (event: ProgressEvent): void =>
        updateFileCollection(buildProgress(event), i, setProgressByFile)
      const onError = (error: Error | null): void =>
        updateFileCollection(error, i, setErrorsByFile)

      await uploadFile(
        direct_upload_url,
        JSON.parse(headers),
        files[i],
        onProgress,
      ).catch(onError)
    }
  }

  return {
    progress,
    progressByFile,
    errorsByFile,
    upload: async (files) => {
      initUpload(files)

      const batches = batch(files, BATCH_SIZE) as File[][]
      let failed = false

      const allBlobs = await Promise.all(
        batches.map(async (batchFiles) => {
          const blobs = await presignBlobs(batchFiles).catch((e: Error) => {
            setErrorsByFile(batchFiles.map(() => e))
            failed = true
          })
          if (!blobs) return
          await uploadFiles(batchFiles, blobs)
          return blobs
        }),
      )

      if (failed) return []
      return allBlobs.flat().filter((blob): blob is PresignedBlob => !!blob)
    },
    resetProgress: ({ skipErrors = false } = {}) => {
      setProgressByFile([])
      if (!skipErrors) setErrorsByFile([])
    },
  }
}
